import { Info } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useI18n } from '../i18n';

/**
 * Info icon with a short explanation. Opens on hover or keyboard focus, and
 * toggles on tap so it also works on touch screens. Escape or an outside tap
 * closes it.
 */
export function InfoTip({ text, label }: { text: string; label?: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setPinned(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setPinned(false);
      }
    };
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      ref={ref}
      className="infotip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => !pinned && setOpen(false)}
    >
      <button
        type="button"
        className="infotip-btn"
        aria-label={label ?? t('tip.more_info')}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onFocus={() => setOpen(true)}
        onBlur={() => !pinned && setOpen(false)}
        onClick={() => {
          // Tap pins the bubble open (touch has no hover); a second tap closes it.
          setPinned(!pinned);
          setOpen(!pinned);
        }}
      >
        <Info />
      </button>
      {open && (
        <span role="tooltip" id={id} className="infotip-bubble">
          {text}
        </span>
      )}
    </span>
  );
}
